// src/components/ProductModal.jsx
import { useState, useEffect } from "react";
import { ShoppingBag, X, Send, Star } from "lucide-react";

export default function ProductModal({ product, onClose }) {
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [reviews, setReviews] = useState([]);
  const [newReview, setNewReview] = useState("");
  const [rating, setRating] = useState(0);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem("accessToken"); // <-- JWT

  // -------------------------------------------------
  // 1. FETCH REVIEWS (with token if present)
  // -------------------------------------------------
  useEffect(() => {
    if (!product?.id) return;

    const headers = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    setLoading(true);
    fetch(`/api/reviews/${product.id}/`, { headers })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load reviews");
        return res.json();
      })
      .then((data) => setReviews(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error(err);
        setReviews([]);
      })
      .finally(() => setLoading(false));
  }, [product.id, token]);

  // -------------------------------------------------
  // 2. STOCK
  // -------------------------------------------------
  const stock = product.stock !== undefined ? product.stock : product.quantity;
  const inStock = stock > 0;
  const lowStock = stock > 0 && stock <= 5;

  // -------------------------------------------------
  // 3. ADD TO CART
  // -------------------------------------------------
  const handleAddToCart = () => {
    if (!inStock) return alert("This product is out of stock!");
    if (product.sizes?.length && !selectedSize) return alert("Please select a size!");
    if (product.colors?.length && !selectedColor) return alert("Please select a color!");

    const cart = JSON.parse(localStorage.getItem("cart") || "[]");
    const existing = cart.find(
      (i) => i.id === product.id && i.size === selectedSize && i.color === selectedColor
    );

    if (existing) {
      if (existing.quantity >= stock) return alert(`Only ${stock} left!`);
      existing.quantity += 1;
    } else {
      cart.push({
        id: product.id,
        name: product.name,
        price: product.offer_price || product.price,
        image: product.image,
        size: selectedSize || null,
        color: selectedColor || null,
        quantity: 1,
      });
    }

    localStorage.setItem("cart", JSON.stringify(cart));
    window.dispatchEvent(new Event("cartUpdated"));
    alert(`${product.name} added to cart!`);
    onClose();
  };

  // -------------------------------------------------
  // 4. SUBMIT REVIEW (with token)
  // -------------------------------------------------
  const handleSubmitReview = async () => {
    if (!newReview.trim()) return alert("Write a review!");
    if (rating === 0) return alert("Select a rating!");

    if (!token) {
      alert("Please login to post a review.");
      // optional: redirect to login
      // navigate("/login");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`/api/reviews/${product.id}/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ text: newReview, rating }),
      });

      const data = await res.json();

      if (res.ok) {
        setReviews([data, ...reviews]);
        setNewReview("");
        setRating(0);
        alert("Review posted!");
      } else {
        alert(data.error || "Failed to post review");
      }
    } catch {
      alert("Network error");
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------
  // 5. RENDER
  // -------------------------------------------------
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-y-auto">
        {/* CLOSE */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="grid md:grid-cols-2 gap-0">
          {/* IMAGE */}
          <div className="relative">
            {product.image ? (
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-96 md:h-full object-cover rounded-t-3xl md:rounded-l-3xl md:rounded-tr-none"
              />
            ) : (
              <div className="w-full h-96 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                <p className="text-gray-500 text-xl">No Image</p>
              </div>
            )}

            {/* Stock Badges */}
            {!inStock && (
              <div className="absolute top-4 left-4 bg-red-600 text-white px-4 py-2 rounded-full font-bold text-sm shadow-lg">
                Out of Stock
              </div>
            )}
            {lowStock && inStock && (
              <div className="absolute top-4 left-4 bg-orange-500 text-white px-4 py-2 rounded-full font-bold text-sm shadow-lg">
                Only {stock} left!
              </div>
            )}
          </div>

          {/* DETAILS */}
          <div className="p-8">
            <h2 className="text-3xl font-bold text-gray-900">{product.name}</h2>

            {/* PRICE & STOCK */}
            <div className="mt-4 flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <span className="text-3xl font-bold text-red-600">
                  ₹{product.offer_price || product.price}
                </span>
                {product.offer_price && (
                  <span className="text-xl text-gray-500 line-through">
                    ₹{product.price}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2">
                {inStock ? (
                  <>
                    <span className="text-green-600 font-semibold">In Stock</span>
                    <span className="text-gray-600">• {stock} available</span>
                    {lowStock && (
                      <span className="text-orange-600 font-medium ml-2">
                        Hurry, low stock!
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-red-600 font-bold">Out of Stock</span>
                )}
              </div>
            </div>

            {/* COLORS */}
            {product.colors?.length > 0 && (
              <div className="mt-8">
                <h3 className="font-semibold text-gray-800 mb-3">Select Color</h3>
                <div className="flex flex-wrap gap-3">
                  {product.colors.map((c) => (
                    <button
                      key={c}
                      onClick={() => setSelectedColor(c)}
                      className={`px-6 py-3 rounded-xl font-medium transition-all border-2 ${
                        selectedColor === c
                          ? "border-black bg-black text-white"
                          : "border-gray-300 hover:border-gray-600"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* SIZES */}
            {product.sizes?.length > 0 && (
              <div className="mt-6">
                <h3 className="font-semibold text-gray-800 mb-3">Select Size</h3>
                <div className="flex flex-wrap gap-3">
                  {product.sizes.map((s) => (
                    <button
                      key={s}
                      onClick={() => setSelectedSize(s)}
                      className={`w-16 h-16 rounded-xl font-bold text-lg transition-all border-2 ${
                        selectedSize === s
                          ? "border-black bg-black text-white"
                          : "border-gray-300 hover:border-gray-600"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ADD TO CART */}
            <button
              onClick={handleAddToCart}
              disabled={!inStock}
              className={`w-full mt-10 py-5 rounded-2xl font-bold text-lg transition flex items-center justify-center gap-3 shadow-xl ${
                inStock
                  ? "bg-black text-white hover:bg-gray-900"
                  : "bg-gray-400 text-gray-700 cursor-not-allowed"
              }`}
            >
              <ShoppingBag className="w-7 h-7" />
              {inStock ? "Add to Cart" : "Out of Stock"}
            </button>

            {/* ---------- REVIEWS ---------- */}
            <div className="mt-12 border-t pt-8">
              <h3 className="text-2xl font-bold mb-6">Customer Reviews</h3>

              {/* WRITE REVIEW */}
              <div className="bg-gray-50 rounded-2xl p-6 mb-8">
                <textarea
                  value={newReview}
                  onChange={(e) => setNewReview(e.target.value)}
                  placeholder="Share your experience..."
                  className="w-full h-28 p-4 border border-gray-300 rounded-xl resize-none focus:ring-2 focus:ring-black outline-none"
                />
                <div className="flex items-center justify-between mt-4">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        onClick={() => setRating(star)}
                        className={`w-8 h-8 cursor-pointer transition-all ${
                          star <= rating
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-gray-300 hover:text-yellow-400"
                        }`}
                      />
                    ))}
                  </div>
                  <button
                    onClick={handleSubmitReview}
                    disabled={loading}
                    className="bg-black text-white px-8 py-3 rounded-xl font-semibold hover:bg-gray-900 transition flex items-center gap-2 disabled:opacity-50"
                  >
                    <Send className="w-5 h-5" />
                    {loading ? "Sending..." : "Submit"}
                  </button>
                </div>
              </div>

              {/* LIST */}
              {loading && reviews.length === 0 ? (
                <p className="text-center text-gray-500">Loading reviews…</p>
              ) : reviews.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  No reviews yet. Be the first!
                </p>
              ) : (
                <div className="space-y-6">
                  {reviews.map((r) => {
                    // ----> Show real name if present
                    const reviewerName =
                      r.user_name ||
                      r.user?.first_name ||
                      r.user?.username ||
                      "Anonymous";

                    return (
                      <div
                        key={r.id}
                        className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-bold text-gray-900">{reviewerName}</p>
                            <div className="flex gap-1 mt-1">
                              {[1, 2, 3, 4, 5].map((s) => (
                                <Star
                                  key={s}
                                  className={`w-5 h-5 ${
                                    s <= r.rating
                                      ? "fill-yellow-400 text-yellow-400"
                                      : "text-gray-300"
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                          <span className="text-sm text-gray-500">
                            {new Date(r.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="mt-4 text-gray-700 leading-relaxed">{r.text}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}